import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import {
  SlotAssetGeneratorState,
  SlotAssetGeneratorActions,
  SlotAssetGenerationRequest,
  SlotAssetResult,
  SavedToast,
  SlotComponentDef,
  SlotComponentGroup,
  AssetFormat,
  SlotGenerationHistoryEntry,
  DEFAULT_GENERATION_STATE,
} from '../../core/types/slot-asset-generator.types';
import type { SlotAssetGeneratorProjectState } from '../../core/types/project-state.types';
import type { AIGeneratorApiKeys, AIProviderKey, ImageStylePreset, ImageQualityPreset, ImageModelId } from '../../core/types/image-generation.types';
import { DEFAULT_API_KEYS } from '../../core/types/image-generation.types';
import { DEFAULT_SLOT_COMPONENTS } from '../constants/slot-asset-components';

enableMapSet();

const generateId = () => Math.random().toString(36).substring(2, 11);

export const useSlotAssetGeneratorStore = create<SlotAssetGeneratorState & SlotAssetGeneratorActions>()(
  persist(
    immer((set, get) => ({
      // === State ===
      components: structuredClone(DEFAULT_SLOT_COMPONENTS),
      selectedComponentId: null,
      generation: { ...DEFAULT_GENERATION_STATE },
      expandedGroups: new Set<string>(['symbols']),
      leftPanelLocked: false,
      leftPanelWidth: 440,
      resultsModalOpen: false,
      lightboxIndex: null,
      toasts: [] as SavedToast[],
      history: [] as SlotGenerationHistoryEntry[],
      historyPanelOpen: false,

      // === API Keys & Image Settings ===
      apiKeys: { ...DEFAULT_API_KEYS } as AIGeneratorApiKeys,
      imageSettings: {
        style: 'realistic' as ImageStylePreset,
        qualityPreset: '1K' as ImageQualityPreset,
        model: 'nano-banana-2' as ImageModelId,
        customStyleText: '',
      },
      isSettingsOpen: false,

      // === API Keys & Settings Actions ===
      setApiKey: (provider: AIProviderKey, key: string) => {
        set((state) => {
          state.apiKeys[provider] = key;
        });
      },

      updateImageSettings: (patch: Partial<{ style: ImageStylePreset; qualityPreset: ImageQualityPreset; model: ImageModelId; customStyleText: string }>) => {
        set((state) => {
          Object.assign(state.imageSettings, patch);
        });
      },

      setQualityPreset: (quality: string) => {
        set((state) => {
          state.imageSettings.qualityPreset = quality as ImageQualityPreset;
        });
      },

      toggleSettings: () => {
        set((state) => {
          state.isSettingsOpen = !state.isSettingsOpen;
        });
      },

      // === Selection ===
      selectComponent: (id: string) => {
        set((state) => {
          state.selectedComponentId = id;
          // Auto-expand the containing group
          const comp = state.components.find((c) => c.id === id);
          if (comp) {
            state.expandedGroups.add(comp.group);
            if (comp.subgroup) {
              state.expandedGroups.add(`${comp.group}:${comp.subgroup}`);
            }
          }
        });
      },

      // === Component CRUD ===
      updateComponent: (id: string, patch: Partial<typeof DEFAULT_SLOT_COMPONENTS[0]>) => {
        set((state) => {
          const idx = state.components.findIndex((c) => c.id === id);
          if (idx !== -1) {
            Object.assign(state.components[idx], patch);
          }
        });
      },

      resetComponentToDefault: (id: string) => {
        set((state) => {
          const defaultComp = DEFAULT_SLOT_COMPONENTS.find((c) => c.id === id);
          if (!defaultComp) return;
          const idx = state.components.findIndex((c) => c.id === id);
          if (idx !== -1) {
            state.components[idx] = structuredClone(defaultComp);
          }
        });
      },

      addCustomComponent: (def) => {
        set((state) => {
          const id = `custom-${generateId()}`;
          state.components.push({
            id,
            name: def.name ?? 'Custom Component',
            group: def.group ?? 'slot' as never,
            subgroup: def.subgroup,
            width: def.width ?? null,
            height: def.height ?? null,
            format: def.format ?? 'png_transparent' as never,
            assetPath: def.assetPath ?? '',
            notes: def.notes ?? '',
            referenceImages: [],
            isCustom: true,
            prompt: def.prompt,
          });
          // Auto-select the new component and expand its group
          state.selectedComponentId = id;
          const group = (def.group ?? 'slot') as string;
          state.expandedGroups.add(group);
          if (def.subgroup) {
            state.expandedGroups.add(`${group}:${def.subgroup}`);
          }
        });
      },

      removeCustomComponent: (id: string) => {
        set((state) => {
          const comp = state.components.find((c) => c.id === id);
          if (!comp?.isCustom) return;
          state.components = state.components.filter((c) => c.id !== id);
          if (state.selectedComponentId === id) {
            state.selectedComponentId = null;
          }
        });
      },

      // === Groups ===
      toggleGroup: (group: string) => {
        set((state) => {
          if (state.expandedGroups.has(group)) {
            state.expandedGroups.delete(group);
          } else {
            state.expandedGroups.add(group);
          }
        });
      },

      setLeftPanelWidth: (width: number) => {
        set((state) => {
          state.leftPanelWidth = Math.max(250, Math.min(600, width));
        });
      },

      // === Generation Lifecycle ===
      startGeneration: (request: SlotAssetGenerationRequest) => {
        set((state) => {
          state.generation.status = 'generating';
          state.generation.request = request;
          state.generation.results = Array.from({ length: request.imageCount }, (_, i) => ({
            id: `result-${generateId()}`,
            imageUrl: '',
            status: 'generating' as const,
          }));
          state.generation.progress = { completed: 0, total: request.imageCount };
          state.generation.error = null;
          state.generation.elapsedMs = 0;
          state.leftPanelLocked = true;
          state.resultsModalOpen = false;
        });
      },

      updateGenerationProgress: (completed: number, total: number) => {
        set((state) => {
          state.generation.progress = { completed, total };
        });
      },

      setGenerationResult: (index: number, result: SlotAssetResult) => {
        set((state) => {
          if (index >= 0 && index < state.generation.results.length) {
            state.generation.results[index] = result;
          }
          // Check if all done
          const allDone = state.generation.results.every(
            (r) => r.status === 'ready' || r.status === 'error' || r.status === 'saved'
          );
          if (allDone) {
            state.generation.status = 'results';
            state.leftPanelLocked = false;
            state.resultsModalOpen = true;
          }
        });
      },

      setGenerationError: (error: string) => {
        set((state) => {
          state.generation.status = 'error';
          state.generation.error = error;
          state.leftPanelLocked = false;
        });
      },

      cancelGeneration: () => {
        set((state) => {
          state.leftPanelLocked = false;
          // Keep already-received images, mark pending as error
          for (const r of state.generation.results) {
            if (r.status === 'generating') {
              r.status = 'error';
              r.error = 'Cancelled';
            }
          }
          const hasReady = state.generation.results.some((r) => r.status === 'ready');
          if (hasReady) {
            state.generation.status = 'results';
            state.resultsModalOpen = true;
          } else {
            state.generation.status = 'idle';
          }
        });
      },

      resetGeneration: () => {
        set((state) => {
          state.generation = { ...DEFAULT_GENERATION_STATE };
          state.leftPanelLocked = false;
          state.resultsModalOpen = false;
          state.lightboxIndex = null;
        });
      },

      // === Results Modal ===
      openResultsModal: () => {
        set((state) => { state.resultsModalOpen = true; });
      },

      closeResultsModal: () => {
        set((state) => {
          state.resultsModalOpen = false;
          state.lightboxIndex = null;
        });
      },

      openLightbox: (index: number) => {
        set((state) => { state.lightboxIndex = index; });
      },

      closeLightbox: () => {
        set((state) => { state.lightboxIndex = null; });
      },

      // === Save ===
      markImageSaved: (resultId: string, savedPath: string) => {
        set((state) => {
          const result = state.generation.results.find((r) => r.id === resultId);
          if (result) {
            result.status = 'saved';
            result.savedPath = savedPath;
          }
        });
      },

      startReroll: (resultId: string) => {
        set((state) => {
          const result = state.generation.results.find((r) => r.id === resultId);
          if (result) {
            result.status = 'generating';
            result.imageUrl = '';
            result.error = undefined;
            result.savedPath = undefined;
          }
        });
      },

      // === Toasts ===
      addToast: (toast: SavedToast) => {
        set((state) => { state.toasts.push(toast); });
      },

      removeToast: (id: string) => {
        set((state) => {
          state.toasts = state.toasts.filter((t) => t.id !== id);
        });
      },

      // === History ===
      addHistoryEntry: (entry: SlotGenerationHistoryEntry) => {
        set((state) => {
          state.history.unshift(entry);
          // Cap at 100 entries
          if (state.history.length > 100) {
            state.history = state.history.slice(0, 100);
          }
        });
      },

      removeHistoryEntry: (id: string) => {
        set((state) => {
          state.history = state.history.filter((h) => h.id !== id);
        });
      },

      clearHistory: () => {
        set((state) => {
          state.history = [];
        });
      },

      toggleHistoryPanel: () => {
        set((state) => {
          state.historyPanelOpen = !state.historyPanelOpen;
        });
      },
    })),
    {
      name: 'slot-asset-generator-storage',
      partialize: (state) => ({
        components: state.components,
        selectedComponentId: state.selectedComponentId,
        expandedGroups: state.expandedGroups,
        leftPanelWidth: state.leftPanelWidth,
        history: state.history,
        historyPanelOpen: state.historyPanelOpen,
        apiKeys: state.apiKeys,
        imageSettings: state.imageSettings,
      }),
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const parsed = JSON.parse(str);
          // Deserialize expandedGroups from array back to Set
          if (parsed?.state?.expandedGroups && Array.isArray(parsed.state.expandedGroups)) {
            parsed.state.expandedGroups = new Set(parsed.state.expandedGroups);
          }
          return parsed;
        },
        setItem: (name, value) => {
          // Serialize Set to array for JSON
          const toStore = {
            ...value,
            state: {
              ...value.state,
              expandedGroups: value.state.expandedGroups instanceof Set
                ? Array.from(value.state.expandedGroups)
                : value.state.expandedGroups,
            },
          };
          localStorage.setItem(name, JSON.stringify(toStore));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
      merge: (persisted, current) => {
        const persistedState = persisted as Partial<SlotAssetGeneratorState> | undefined;
        if (!persistedState) return current;

        return {
          ...current,
          ...persistedState,
          // Ensure expandedGroups is a Set
          expandedGroups: persistedState.expandedGroups instanceof Set
            ? persistedState.expandedGroups
            : new Set(Array.isArray(persistedState.expandedGroups)
                ? persistedState.expandedGroups
                : ['symbols']),
          // Restore leftPanelWidth with fallback
          leftPanelWidth: persistedState.leftPanelWidth ?? 440,
          // Restore history
          history: Array.isArray(persistedState.history) ? persistedState.history : [],
          historyPanelOpen: persistedState.historyPanelOpen ?? false,
          // Restore API keys and image settings
          apiKeys: persistedState.apiKeys ?? { ...DEFAULT_API_KEYS },
          imageSettings: persistedState.imageSettings ?? {
            style: 'realistic' as ImageStylePreset,
            qualityPreset: '1K' as ImageQualityPreset,
            model: 'nano-banana-2' as ImageModelId,
            customStyleText: '',
          },
          // Always reset transient state
          generation: { ...DEFAULT_GENERATION_STATE },
          isSettingsOpen: false,
          leftPanelLocked: false,
          resultsModalOpen: false,
          lightboxIndex: null,
          toasts: [],
        };
      },
    },
  ),
);

/**
 * Compute the diff between current components and defaults.
 * Only stores fields that differ from the default registry.
 */
export function computeSlotAssetGeneratorDiff(
  components: SlotComponentDef[]
): SlotAssetGeneratorProjectState {
  const overrides: SlotAssetGeneratorProjectState['componentOverrides'] = {};
  const customComponents: SlotAssetGeneratorProjectState['customComponents'] = [];
  const promptHistory: SlotAssetGeneratorProjectState['promptHistory'] = {};

  for (const comp of components) {
    if (comp.isCustom) {
      customComponents.push({
        id: comp.id,
        name: comp.name,
        group: comp.group,
        subgroup: comp.subgroup,
        width: comp.width,
        height: comp.height,
        format: comp.format,
        assetPath: comp.assetPath,
        notes: comp.notes,
        referenceImages: comp.referenceImages,
        prompt: comp.prompt,
      });
      continue;
    }

    const defaultComp = DEFAULT_SLOT_COMPONENTS.find((d) => d.id === comp.id);
    if (!defaultComp) continue;

    const diff: Record<string, unknown> = {};
    if (comp.name !== defaultComp.name) diff.name = comp.name;
    if (comp.width !== defaultComp.width) diff.width = comp.width;
    if (comp.height !== defaultComp.height) diff.height = comp.height;
    if (comp.format !== defaultComp.format) diff.format = comp.format;
    if (comp.notes !== defaultComp.notes) diff.notes = comp.notes;
    if (comp.prompt) diff.prompt = comp.prompt;
    if (comp.referenceImages.length > 0) diff.referenceImages = comp.referenceImages;

    if (Object.keys(diff).length > 0) {
      overrides[comp.id] = diff;
    }

    // Track prompt history
    if (comp.prompt) {
      promptHistory[comp.id] = [comp.prompt];
    }
  }

  return {
    version: 1,
    lastModified: Date.now(),
    componentOverrides: overrides,
    customComponents,
    promptHistory,
  };
}

/**
 * Apply persisted overrides onto default components.
 * New defaults appear for unmodified components; user edits are preserved.
 */
export function applySlotAssetGeneratorState(
  persisted: SlotAssetGeneratorProjectState
): SlotComponentDef[] {
  // Start with fresh defaults
  const components = structuredClone(DEFAULT_SLOT_COMPONENTS);

  // Apply overrides
  for (const [id, override] of Object.entries(persisted.componentOverrides)) {
    const comp = components.find((c) => c.id === id);
    if (comp) {
      Object.assign(comp, override);
    }
  }

  // Append custom components
  for (const custom of persisted.customComponents) {
    components.push({
      ...custom,
      format: custom.format as AssetFormat,
      group: custom.group as SlotComponentGroup,
      isCustom: true,
    } as SlotComponentDef);
  }

  return components;
}
